import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Calculator, CheckCircle, Warning } from "@phosphor-icons/react";
import RecipeCombobox from "@/components/RecipeCombobox";

export default function BatchCalculator() {
  const [params] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [recipeId, setRecipeId] = useState(params.get("recipe") || "");
  const [batchSize, setBatchSize] = useState(30);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/inventory/recipes");
      setRecipes(data || []);
    })();
  }, []);

  const run = async () => {
    if (!recipeId) return toast.error("Pick a recipe first");
    setLoading(true);
    try {
      const { data } = await api.post("/inventory/batch-calculator/preview", {
        recipe_id: recipeId,
        batch_size: Number(batchSize),
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  const grouped = result
    ? result.items.reduce((acc, it) => {
        const key = it.section || "INGREDIENTS";
        (acc[key] = acc[key] || []).push(it);
        return acc;
      }, {})
    : {};

  return (
    <div className="space-y-5" data-testid="inv-calc-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
          Inventory · Production
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Batch Size Calculator</h1>
        <p className="text-sm text-slate-500 mt-1">
          Pick a recipe, enter your batch size, and we&apos;ll compute ingredient requirements, shortages, and estimated cost.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Recipe</span>
            <RecipeCombobox
              recipes={recipes}
              value={recipeId}
              onPick={setRecipeId}
              testId="calc-recipe-select"
              placeholder="Select a recipe…"
              className="h-10"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Batch size (kg of finished product)</span>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="calc-batch-size"
            />
          </label>
          <button
            onClick={run}
            disabled={loading}
            className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="calc-run-btn"
          >
            <Calculator size={14} weight="bold" />
            {loading ? "Calculating…" : "Calculate"}
          </button>
        </div>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Recipe" value={result.recipe.name} subtle />
            <Tile label="Batch Size" value={`${result.batch_size} kg`} />
            <Tile
              label="Status"
              value={result.can_produce ? "Producible" : "Shortage"}
              tone={result.can_produce ? "emerald" : "rose"}
              icon={result.can_produce ? CheckCircle : Warning}
            />
            <Tile label="Est. Cost" value={`₹${Number(result.estimated_cost).toLocaleString("en-IN")}`} />
          </div>

          {Object.keys(grouped).map((section) => (
            <div key={section} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.22em] text-slate-600 font-bold">
                {section}
              </div>
              <table className="w-full">
                <thead>
                  <tr>
                    {["Ingredient", "Brand", "Qty/kg", "Required", "Available", "Shortage", "Line ₹"].map((h) => (
                      <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grouped[section].map((it, idx) => (
                    <tr key={`${it.item_id}-${idx}`} className="border-t border-slate-100" data-testid={`calc-row-${it.item_id}`}>
                      <td className="px-4 py-2 text-sm font-medium text-slate-900">{it.ingredient_name}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{it.brand}</td>
                      <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">{it.qty_per_kg}</td>
                      <td className="px-4 py-2 text-sm tabular-nums">{it.required} {it.uom}</td>
                      <td className={`px-4 py-2 text-sm tabular-nums ${it.available <= 0 ? "text-rose-600" : "text-slate-700"}`}>{it.available} {it.uom}</td>
                      <td className={`px-4 py-2 text-sm tabular-nums font-medium ${it.shortage > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                        {it.shortage > 0 ? `-${it.shortage}` : "0"}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500 tabular-nums">₹{it.line_cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, tone = "slate", icon: Icon, subtle }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className={`mt-1 flex items-center gap-2 ${subtle ? "text-base font-semibold text-slate-900" : "text-2xl font-bold text-slate-900"}`}>
        {Icon && <span className={`w-7 h-7 rounded ${toneMap[tone]} flex items-center justify-center`}><Icon size={14} weight="bold" /></span>}
        {value}
      </div>
    </div>
  );
}
